// Estilos do cabeçalho de informações do ticket (M5.4b) da modernização do
// InfoChat.
//
// Função pura — sem JSX, sem I/O, sem leitura de localStorage/backend.
// Recebe o `theme` do MUI (idealmente já com `theme.infoChat` da Entrega
// M2) e devolve só objetos de estilo simples (CSS-in-JS), para serem
// mesclados nas classes já existentes de `TicketInfo`. Não aplica nada
// sozinha — quem decide onde/como usar é esse arquivo.
//
// Helper independente das demais entregas (M5.1/M5.2/M5.3a/M5.3b/M5.4a) —
// por decisão explícita, não importa nem se acopla a elas, mesmo definindo
// um `focusRingAccent` equivalente (duplicação intencional, cada entrega
// fica isolada).
//
// Escopo desta entrega (M5.4b): exclusivamente o anel de foco visível do
// título clicável (alternativa de teclado ao `CardHeader`, que permanece
// sem semântica interativa própria) e do avatar clicável (quando há foto)
// — controles irmãos no DOM, nunca um aninhado dentro do outro. Nenhuma
// outra decisão visual — o componente não tinha nenhuma cor customizada
// além do hover do avatar, e continua sem nenhuma, além do anel de foco.
//
// Usa exclusivamente os tokens de `theme.infoChat` para cor — nunca um hex
// literal novo aqui.

import { createInfoChatDesignTokens } from "../theme/infoChatDesignTokens";

const hasCompleteInfoChatTokens = tokens =>
  Boolean(
    tokens &&
      tokens.brand &&
      tokens.surfaces &&
      tokens.borders &&
      tokens.text &&
      tokens.shadows &&
      tokens.transitions &&
      tokens.radii &&
      tokens.spacing &&
      tokens.status
  );

// Mesmo padrão de fallback seguro já usado nas entregas anteriores — nunca
// deixa o cabeçalho quebrar caso theme.infoChat esteja ausente/incompleto,
// e nunca diverge dos tokens reais por reaproveitar a mesma função pura da
// M2.
const resolveInfoChatTokens = theme => {
  if (hasCompleteInfoChatTokens(theme?.infoChat)) {
    return theme.infoChat;
  }
  return createInfoChatDesignTokens(theme?.palette?.type, theme?.palette?.primary?.main);
};

/**
 * Estilos do cabeçalho de informações do ticket modernizado (M5.4b).
 *
 * @param {object} theme - tema do MUI (idealmente já com `theme.infoChat`).
 */
export const createInfoChatTicketInfoStyles = theme => {
  const tokens = resolveInfoChatTokens(theme);

  return {
    // Anel de foco visível (navegação real por teclado) — mesma cor
    // institucional, mesmo raciocínio já corrigido em
    // M4/M5.1/M5.2/M5.3a/M5.3b/M5.4a: usa `outline`/`outlineOffset`, nunca
    // `boxShadow` ou `backgroundColor`, para nunca disputar a mesma
    // propriedade usada por um eventual estado de hover — o foco sempre
    // aparece por cima de qualquer estado, sem sobrescrever nada. Aplicado
    // via `:focus-visible` (não `:focus`) porque o título (Typography via
    // `titleTypographyProps`) e o avatar aqui têm `role="button"` próprio,
    // não `ButtonBase` do MUI — não recebem `Mui-focusVisible`
    // automaticamente, então o anel usa o equivalente nativo do navegador
    // (`:focus-visible`), que também só aparece em navegação real por
    // teclado, nunca em clique de mouse.
    focusRingAccent: {
      outline: `2px solid ${tokens.brand.primaryColor}`,
      outlineOffset: "-2px"
    }
  };
};

export default createInfoChatTicketInfoStyles;
